import { CSS } from '@dnd-kit/utilities'
import type { List } from 'immutable'
import { useSelector } from 'react-redux'
import { useSortable } from '@dnd-kit/sortable'

import { ScrollIntoViewAfterDropped } from '../../utils/hooks/useSortableHelpers'
import { useTranslate } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'

type ListItemProps = {
  index: number
  name: string
  id: string
}

export function ListItem({ index, name, id }: Readonly<ListItemProps>) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(
      transform
        ? {
            ...transform,
            // https://github.com/clauderic/dnd-kit/issues/1528
            scaleX: 1,
            scaleY: 1,
          }
        : null,
    ),
    transition,
  }

  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))

  const t = useTranslate('shop')
  const interfaceT = useTranslate('interface', 'components.bottomBar.footerSettings.sections.popovers.pageSelection')

  function translatePage(page: ImmutableMap): ImmutableMap {
    return page.set('title', isBeyondShop ? t(page.get('title')) : page.get('title'))
  }

  return (
    <FormValue name={name}>
      {(pages: List<ImmutableMap>, setPages: (newPages: List<ImmutableMap>) => void) => {
        const page = translatePage(pages.get(index))

        function togglePage() {
          setPages(pages.set(index, page.update('isVisible', (v) => !v).set('isPreview', true)))
        }

        return (
          <div
            id={id}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={'ep-footer-sections-settings-popover-item ep-footer-sections-settings-popover-pages-item'}
          >
            <ScrollIntoViewAfterDropped id={id} />
            <input
              // https://github.com/clauderic/dnd-kit/issues/913
              onKeyDown={(e) => e.stopPropagation()}
              type="checkbox"
              checked={page.get('isVisible')}
              title={
                page.get('isVisible') ? interfaceT('hideItemButton.tooltip') : interfaceT('showItemButton.tooltip')
              }
              onChange={togglePage}
              data-testid={`toggle-${page.get('id')}`}
            ></input>
            <span className="ep-footer-sections-settings-text">{page.get('title')}</span>
          </div>
        )
      }}
    </FormValue>
  )
}
